import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { ConversationView, type ConversationMessage } from './ConversationView';

interface StageDetailDialogProps {
  open: boolean;
  onClose: () => void;
  runId: string;
  stageKey: string;
}

function stageLabel(value: string): string {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function StageDetailDialog({ open, onClose, runId, stageKey }: StageDetailDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!open || !runId || !stageKey) return;
    setLoading(true);
    setError(null);
    void apiClient.getAiWorkflowStageDetail(runId, stageKey)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load stage detail'))
      .finally(() => setLoading(false));
  }, [open, runId, stageKey]);

  const conversation = useMemo<ConversationMessage[]>(() => {
    const messages = Array.isArray(data?.conversation) ? data.conversation : [];
    return messages.map((entry: any, index: number) => ({
      id: String(entry?.id || `${stageKey}-${index}`),
      role: entry?.role === 'system' ? 'system' : entry?.role === 'user' ? 'user' : entry?.role === 'tool' ? 'tool' : 'assistant',
      content: String(entry?.content || ''),
      timestamp: typeof entry?.timestamp === 'string' ? entry.timestamp : undefined,
      stage: typeof entry?.stage === 'string' ? stageLabel(entry.stage) : stageLabel(stageKey),
      toolName: typeof entry?.toolName === 'string' ? entry.toolName : undefined,
      toolInput: typeof entry?.toolInput === 'string' ? entry.toolInput : undefined,
      toolOutput: typeof entry?.toolOutput === 'string' ? entry.toolOutput : undefined,
      durationMs: typeof entry?.durationMs === 'number' ? entry.durationMs : undefined,
    }));
  }, [data, stageKey]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              {stageLabel(stageKey)}
              {data?.displayEntry?.status ? (
                <Badge variant={data.displayEntry.status === 'failed' ? 'destructive' : data.displayEntry.status === 'completed' ? 'success' : 'outline'}>
                  {stageLabel(data.displayEntry.status)}
                </Badge>
              ) : null}
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {loading && (
            <div className="space-y-2">
              <Skeleton className="h-20 rounded" />
              <Skeleton className="h-24 rounded" />
              <Skeleton className="h-56 rounded" />
            </div>
          )}
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <>
              <section className="grid gap-2 sm:grid-cols-2">
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Status</p>
                  <p className="text-sm">{stageLabel(data?.displayEntry?.status || 'pending')}</p>
                </div>
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Timestamp</p>
                  <p className="text-sm">{data?.displayEntry?.timestamp ? new Date(data.displayEntry.timestamp).toLocaleString() : '-'}</p>
                </div>
              </section>
              <section className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Agent Conversation</p>
                <ConversationView messages={conversation} emptyLabel="No stage conversation captured." />
              </section>
              <section className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Attempts</p>
                {(data?.attempts || []).length === 0 ? (
                  <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No attempts captured.
                  </div>
                ) : (data.attempts || []).map((attempt: any) => (
                  <div key={attempt.attemptId} className="rounded border border-gray-200 p-3 dark:border-gray-700">
                    <div className="mb-1 flex items-center gap-2">
                      <Badge variant={attempt.status === 'failed' ? 'destructive' : attempt.status === 'completed' ? 'success' : 'outline'}>
                        {stageLabel(attempt.status)}
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{attempt.action}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{attempt.decisionSummary || attempt.triggerReason}</p>
                  </div>
                ))}
              </section>
              {(data?.events || []).length > 0 && (
                <section className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Decision Events</p>
                  {(data.events || []).map((event: any) => (
                    <div key={event.id} className="rounded border border-gray-200 p-2 text-xs dark:border-gray-700">
                      <p className="font-medium text-gray-700 dark:text-gray-300">{stageLabel(event.type || 'event')}</p>
                      <p className="text-gray-600 dark:text-gray-400">{event.reason}</p>
                    </div>
                  ))}
                </section>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
